import { Divider, Flex, Group } from '@byecode/ui'
import type { FlowNode, NodeTypes, WechatOpenLocationActionConfig } from '@lighthouse/shared'
import {
    DEVICE_ID_OPTION,
    getAllCanBeUpstreamNodes,
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    NodeFieldPreview,
    PAGE_LINK_OPTION,
    useAtomData
} from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { workflowListAtom } from '@/atoms/workflow/state'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import * as SC from './styles'

interface WechatOpenLocationActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes: FlowNode[]
    value: WechatOpenLocationActionConfig
    onChange: (value: WechatOpenLocationActionConfig) => void
}

export const WechatOpenLocationActionConfigure: React.FunctionComponent<WechatOpenLocationActionConfigureProps> = ({
    allParentNodes,
    value,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const { type } = useFlowContainerContext()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const disabledWithVersion = useIsDisabledWithVersion()

    const upstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                upstreamNodes: getAllCanBeUpstreamNodes(allParentNodes),
                allUpstreamNodes: allParentNodes,
                dataSourceList
            }),
        [allParentNodes, dataSourceList, workflowList]
    )

    const userOption = useGetUserRecordOptions()

    const isNotAutoMation = type !== 'automation'

    return (
        <>
            <Group label="经纬度">
                <SC.FieldList>
                    <SC.FieldItem>
                        <PopoverQuestion
                            type="longitude"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <SC.FieldTitle required>经度</SC.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('NUMBER')}
                            disabled={disabledWithVersion}
                            placeholder="请输入"
                            value={value.longitude}
                            onBlur={longitude => onChange({ ...value, longitude })}
                            autoHeight
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            pageOptions={isNotAutoMation ? [PAGE_LINK_OPTION, DEVICE_ID_OPTION] : undefined}
                            dataSourceList={dataSourceList}
                        />
                    </SC.FieldItem>
                    <SC.FieldItem>
                        <PopoverQuestion
                            type="latitude"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <SC.FieldTitle required>维度</SC.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('NUMBER')}
                            disabled={disabledWithVersion}
                            placeholder="请输入"
                            value={value.latitude}
                            onBlur={latitude => onChange({ ...value, latitude })}
                            autoHeight
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            pageOptions={isNotAutoMation ? [PAGE_LINK_OPTION, DEVICE_ID_OPTION] : undefined}
                            dataSourceList={dataSourceList}
                        />
                    </SC.FieldItem>
                </SC.FieldList>
            </Group>
            <Divider color="var(--color-gray-200)" />
            <Group label="地图外观">
                <SC.FieldList>
                    <SC.FieldItem>
                        <PopoverQuestion
                            type="scale"
                            renderTarget={(node, ref) => (
                                <Flex gap={6} ref={ref} alignItems="center">
                                    <SC.FieldTitle>缩放比例</SC.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('NUMBER')}
                            disabled={disabledWithVersion}
                            placeholder="请输入"
                            value={value.scale}
                            onBlur={scale => onChange({ ...value, scale })}
                            autoHeight
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            pageOptions={isNotAutoMation ? [PAGE_LINK_OPTION, DEVICE_ID_OPTION] : undefined}
                            dataSourceList={dataSourceList}
                        />
                    </SC.FieldItem>
                    <SC.FieldItem>
                        <Flex gap={16}>
                            <SC.FieldTitle>位置名</SC.FieldTitle>
                        </Flex>

                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            placeholder="请输入"
                            value={value.name}
                            onBlur={name => onChange({ ...value, name })}
                            autoHeight
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            pageOptions={isNotAutoMation ? [PAGE_LINK_OPTION, DEVICE_ID_OPTION] : undefined}
                            dataSourceList={dataSourceList}
                        />
                    </SC.FieldItem>
                    <SC.FieldItem>
                        <Flex gap={16}>
                            <SC.FieldTitle>地址说明</SC.FieldTitle>
                        </Flex>

                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            placeholder="请输入"
                            value={value.address}
                            onBlur={address => onChange({ ...value, address })}
                            autoHeight
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            pageOptions={isNotAutoMation ? [PAGE_LINK_OPTION, DEVICE_ID_OPTION] : undefined}
                            dataSourceList={dataSourceList}
                        />
                    </SC.FieldItem>
                </SC.FieldList>
            </Group>
        </>
    )
}
